import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Card,
  CardContent,
  Fade,
  Alert,
  AlertTitle,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SelectCmp from "react-select";
import { reportStores } from "../../constants";
import moment from "moment";
import * as commonActions from "../../containers/common/actions";
import { toastError, toastSuccess } from "../../utils";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import StorefrontIcon from "@mui/icons-material/Storefront";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import SEO from "../../components/SEO";

const PageContainer = styled(Box)({
  minHeight: "calc(100vh - 64px)",
  background: "linear-gradient(135deg, #e8eef3 0%, #dce4ec 100%)",
  padding: "32px 24px",
  position: "relative",
});

const PageHeader = styled(Box)({
  textAlign: "center",
  marginBottom: "32px",
  "& h1": {
    fontSize: "2.25rem",
    fontWeight: 700,
    background: "linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "8px",
    fontFamily: "'DM Sans', sans-serif",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
  },
  "& p": {
    fontSize: "1rem",
    color: "#5f6368",
    maxWidth: "700px",
    margin: "0 auto",
    lineHeight: 1.6,
  },
});

const StyledCard = styled(Card)({
  borderRadius: "16px",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  background: "white",
  marginBottom: "24px",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
  },
});

const StyledButton = styled(Button)({
  fontFamily: "'DM Sans', sans-serif",
  background: "linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)",
  fontSize: "1rem",
  height: "48px",
  minWidth: "180px",
  color: "white",
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "12px",
  boxShadow: "0 4px 12px rgba(26, 115, 232, 0.3)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1557b0 0%, #00a589 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 16px rgba(26, 115, 232, 0.4)",
  },
  "&:disabled": {
    background: "linear-gradient(135deg, #9e9e9e 0%, #757575 100%)",
    color: "rgba(255, 255, 255, 0.7)",
    cursor: "not-allowed",
    transform: "none",
    boxShadow: "none",
  },
});

const SectionLabel = styled(Typography)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "#5f6368",
  marginBottom: "8px",
  display: "flex",
  alignItems: "center",
  gap: "6px",
});

const StyledTableContainer = styled(TableContainer)({
  borderRadius: "16px",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  backgroundColor: "white",
  overflow: "hidden",
  "& .MuiTable-root": {
    fontFamily: "'DM Sans', sans-serif",
    backgroundColor: "white",
  },
  "& .MuiTableHead-root": {
    background: "linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)",
    "& .MuiTableCell-head": {
      color: "white",
      fontWeight: 700,
      fontSize: "0.9375rem",
      fontFamily: "'DM Sans', sans-serif",
      padding: "16px",
    },
  },
  "& .MuiTableBody-root": {
    backgroundColor: "white",
    "& .MuiTableRow-root": {
      backgroundColor: "white !important",
      transition: "background-color 0.2s ease",
      "&:nth-of-type(odd)": {
        backgroundColor: "#f9f9f9 !important",
      },
      "&:hover": {
        backgroundColor: "#f0f7ff !important",
      },
    },
  },
  "& .MuiTableCell-body": {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "0.875rem",
    color: "#202124",
    padding: "16px",
    borderBottom: "1px solid #f0f0f0",
  },
});

const StatusChip = styled(Chip)(({ statustype }) => {
  const getStatusColor = () => {
    switch (statustype) {
      case "received":
        return { bg: "#4caf50", color: "#ffffff" };
      case "rejected":
        return { bg: "#f44336", color: "#ffffff" };
      case "pending":
      default:
        return { bg: "#ff9800", color: "#ffffff" };
    }
  };

  const colors = getStatusColor();

  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 600,
    fontSize: "0.75rem",
    height: "28px",
    fontFamily: "'DM Sans', sans-serif",
  };
});

export default function Reports(props) {
  const [reportDate, setReportDate] = useState({
    start_date: null,
    end_date: null,
    store: "All Stores",
  });
  const [reports, setReports] = useState([]);

  // Check if there's any pending report (status === 0)
  const hasPendingReport =
    Array.isArray(reports) && reports.some((report) => report.status === 0);

  useEffect(() => {
    onGetReportList();
  }, []);

  const onGetReportList = () => {
    props.setLoader(true);
    commonActions
      .requestReportList()
      .then((res) => {
        props.setLoader(false);
        setReports(Array.isArray(res.body) ? res.body.reverse() : []);
      })
      .catch((err) => {
        props.setLoader(false);
      });
  };

  const setReportDates = (key, date) => {
    if (date && moment.isMoment(date)) {
      setReportDate({ ...reportDate, [key]: date.valueOf() });
    } else if (date) {
      setReportDate({ ...reportDate, [key]: moment(date).valueOf() });
    }
  };

  const onSubmitReport = () => {
    if (!reportDate.store) {
      return toastError("Please select store");
    }
    if (!reportDate.start_date) {
      return toastError("Please select Start month");
    }
    if (!reportDate.end_date) {
      return toastError("Please select End month");
    }
    // Validate end date is not before start date
    if (moment(reportDate.end_date).isBefore(moment(reportDate.start_date))) {
      return toastError("End month cannot be before Start month");
    }
    props.setLoader(true);
    commonActions
      .requestReport(reportDate)
      .then((res) => {
        props.setLoader(false);
        toastSuccess(res.message);
        onGetReportList();
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err.message);
      });
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      background: "white",
      borderRadius: "12px",
      minHeight: "56px",
      borderColor: "#d0d7de",
      borderWidth: "2px",
      fontFamily: "'DM Sans', sans-serif",
      fontSize: "1rem",
      "&:hover": {
        borderColor: "#1a73e8",
      },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    singleValue: (defaultStyles) => ({
      ...defaultStyles,
      color: "#202124",
      fontFamily: "'DM Sans', sans-serif",
    }),
    placeholder: (defaultStyles) => ({
      ...defaultStyles,
      color: "#80868b",
      fontFamily: "'DM Sans', sans-serif",
    }),
    option: (defaultStyles, state) => ({
      ...defaultStyles,
      backgroundColor: state.isDisabled
        ? "#f5f5f5"
        : state.isSelected
        ? "#1a73e8"
        : state.isFocused
        ? "#e3f2fd"
        : "white",
      color: state.isDisabled
        ? "#bdbdbd"
        : state.isSelected
        ? "white"
        : "#202124",
      fontFamily: "'DM Sans', sans-serif",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      textDecoration: state.isDisabled ? "line-through" : "none",
      "&:active": {
        backgroundColor: state.isDisabled ? "#f5f5f5" : "#1a73e8",
      },
    }),
  };

  return (
    <PageContainer>
      <SEO
        title="Streaming Reports"
        description="Request and track your streaming reports from various music platforms and services."
      />
      <Fade in timeout={800}>
        <Box>
          {/* Page Header */}
          <PageHeader>
            <Typography variant="h1">
              <AssessmentIcon sx={{ fontSize: 40, color: "#1a73e8" }} />
              Streaming Reports
            </Typography>
            <Typography>
              Request and track your streaming reports from various music
              platforms
            </Typography>
          </PageHeader>

          {/* Request Report Card */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} lg={10} sx={{ margin: "auto" }}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: "#202124",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    Request New Report
                  </Typography>

                  {/* Pending Request Alert */}
                  {hasPendingReport && (
                    <Alert
                      severity="info"
                      icon={<PendingIcon />}
                      sx={{
                        mb: 3,
                        borderRadius: "12px",
                        "& .MuiAlert-message": {
                          fontFamily: "'DM Sans', sans-serif",
                        },
                      }}
                    >
                      <AlertTitle sx={{ fontWeight: 600 }}>
                        Request Pending
                      </AlertTitle>
                      You have a pending report request. Only one report request
                      can be processed at a time. Please wait for your current
                      request to be completed before submitting a new one.
                    </Alert>
                  )}

                  {/* Warning Alert */}
                  <Alert
                    severity="warning"
                    icon={<WarningAmberIcon />}
                    sx={{
                      mb: 3,
                      borderRadius: "12px",
                      "& .MuiAlert-message": {
                        fontFamily: "'DM Sans', sans-serif",
                      },
                    }}
                  >
                    <AlertTitle sx={{ fontWeight: 600 }}>
                      Important Notice
                    </AlertTitle>
                    Streaming services usually send these reports on a 3-4 month
                    delay. Please keep this in mind when requesting reports.
                  </Alert>

                  <Grid container spacing={3}>
                    {/* Store Selection */}
                    <Grid item xs={12}>
                      <SectionLabel>
                        <StorefrontIcon sx={{ fontSize: 18 }} />
                        Select Store
                      </SectionLabel>
                      <SelectCmp
                        // options={reportStores}
                        options={[{
                          label: "All Stores",
                          value: "All Stores",
                        }]}
                        value={
                          reportDate.store
                            ? {
                                label: reportDate.store,
                                value: reportDate.store,
                              }
                            : null
                        }
                        onChange={(e) =>
                          setReportDate({ ...reportDate, store: e.value })
                        }
                        styles={selectStyles}
                        placeholder="Choose a streaming platform..."
                        menuPortalTarget={document.body}
                      />
                    </Grid>

                    {/* Start Month */}
                    <Grid item xs={12} sm={6}>
                      <SectionLabel>
                        <CalendarTodayIcon sx={{ fontSize: 18 }} />
                        Start Month
                      </SectionLabel>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <SelectCmp
                            options={Array.from({ length: 12 }, (_, i) => ({
                              value: i,
                              label: moment().month(i).format("MMMM"),
                            }))}
                            value={
                              reportDate.start_date
                                ? {
                                    value: moment(
                                      reportDate.start_date
                                    ).month(),
                                    label: moment(reportDate.start_date).format(
                                      "MMMM"
                                    ),
                                  }
                                : null
                            }
                            onChange={(e) => {
                              const currentYear = reportDate.start_date
                                ? moment(reportDate.start_date).year()
                                : moment().year();
                              const newDate = moment()
                                .year(currentYear)
                                .month(e.value)
                                .startOf("month");
                              setReportDate({
                                ...reportDate,
                                start_date: newDate.valueOf(),
                              });
                            }}
                            styles={selectStyles}
                            placeholder="Select Month"
                            menuPortalTarget={document.body}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <SelectCmp
                            options={Array.from({ length: 21 }, (_, i) => {
                              const year = moment().year() - 10 + i;
                              return { value: year, label: year.toString() };
                            })}
                            value={
                              reportDate.start_date
                                ? {
                                    value: moment(reportDate.start_date).year(),
                                    label: moment(reportDate.start_date)
                                      .year()
                                      .toString(),
                                  }
                                : null
                            }
                            onChange={(e) => {
                              const currentMonth = reportDate.start_date
                                ? moment(reportDate.start_date).month()
                                : 0;
                              const newDate = moment()
                                .year(e.value)
                                .month(currentMonth)
                                .startOf("month");
                              setReportDate({
                                ...reportDate,
                                start_date: newDate.valueOf(),
                              });
                            }}
                            styles={selectStyles}
                            placeholder="Select Year"
                            menuPortalTarget={document.body}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* End Month */}
                    <Grid item xs={12} sm={6}>
                      <SectionLabel>
                        <CalendarTodayIcon sx={{ fontSize: 18 }} />
                        End Month
                      </SectionLabel>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <SelectCmp
                            options={Array.from({ length: 12 }, (_, i) => {
                              const startYear = reportDate.start_date
                                ? moment(reportDate.start_date).year()
                                : null;
                              const startMonth = reportDate.start_date
                                ? moment(reportDate.start_date).month()
                                : null;
                              const endYear = reportDate.end_date
                                ? moment(reportDate.end_date).year()
                                : moment().year();

                              // Disable months if end year equals start year and month is before start month
                              const isDisabled =
                                startYear &&
                                endYear === startYear &&
                                i < startMonth;

                              return {
                                value: i,
                                label: moment().month(i).format("MMMM"),
                                isDisabled: isDisabled,
                              };
                            })}
                            value={
                              reportDate.end_date
                                ? {
                                    value: moment(reportDate.end_date).month(),
                                    label: moment(reportDate.end_date).format(
                                      "MMMM"
                                    ),
                                  }
                                : null
                            }
                            onChange={(e) => {
                              const currentYear = reportDate.end_date
                                ? moment(reportDate.end_date).year()
                                : moment().year();
                              const newDate = moment()
                                .year(currentYear)
                                .month(e.value)
                                .startOf("month");
                              setReportDate({
                                ...reportDate,
                                end_date: newDate.valueOf(),
                              });
                            }}
                            styles={selectStyles}
                            placeholder="Select Month"
                            menuPortalTarget={document.body}
                            isOptionDisabled={(option) => option.isDisabled}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <SelectCmp
                            options={Array.from({ length: 21 }, (_, i) => {
                              const year = moment().year() - 10 + i;
                              const startYear = reportDate.start_date
                                ? moment(reportDate.start_date).year()
                                : null;

                              // Disable years that are before start year
                              const isDisabled = startYear && year < startYear;

                              return {
                                value: year,
                                label: year.toString(),
                                isDisabled: isDisabled,
                              };
                            })}
                            value={
                              reportDate.end_date
                                ? {
                                    value: moment(reportDate.end_date).year(),
                                    label: moment(reportDate.end_date)
                                      .year()
                                      .toString(),
                                  }
                                : null
                            }
                            onChange={(e) => {
                              const currentMonth = reportDate.end_date
                                ? moment(reportDate.end_date).month()
                                : 0;
                              const startYear = reportDate.start_date
                                ? moment(reportDate.start_date).year()
                                : null;
                              const startMonth = reportDate.start_date
                                ? moment(reportDate.start_date).month()
                                : null;

                              // If selecting a year and it equals start year, ensure month is not before start month
                              let finalMonth = currentMonth;
                              if (
                                startYear &&
                                e.value === startYear &&
                                startMonth !== null &&
                                currentMonth < startMonth
                              ) {
                                finalMonth = startMonth;
                              }

                              const newDate = moment()
                                .year(e.value)
                                .month(finalMonth)
                                .startOf("month");
                              setReportDate({
                                ...reportDate,
                                end_date: newDate.valueOf(),
                              });
                            }}
                            styles={selectStyles}
                            placeholder="Select Year"
                            menuPortalTarget={document.body}
                            isOptionDisabled={(option) => option.isDisabled}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Submit Button */}
                    <Grid item xs={12}>
                      <Box textAlign="center" mt={2}>
                        <StyledButton
                          onClick={onSubmitReport}
                          disabled={hasPendingReport}
                        >
                          Request Report
                        </StyledButton>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>

          {/* Previously Requested Reports */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#202124",
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              <HistoryIcon sx={{ color: "#1a73e8" }} />
              Previously Requested Reports
            </Typography>
          </Box>

          <StyledTableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>No.</TableCell>
                  <TableCell>Report For</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(reports) && reports.length ? (
                  reports.map((item, key) => {
                    // Determine status display
                    let statusType = "pending";
                    let statusLabel = "PENDING";
                    let statusIcon = <PendingIcon />;

                    if (item.status === 1) {
                      statusType = "received";
                      statusLabel = "RECEIVED";
                      statusIcon = <CheckCircleIcon />;
                    } else if (item.status === 2) {
                      statusType = "rejected";
                      statusLabel = "REJECTED";
                      statusIcon = <ErrorIcon />;
                    }

                    return (
                      <TableRow key={key}>
                        <TableCell>{key + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {item.store}
                        </TableCell>
                        <TableCell>
                          {moment(Number(item.start_date)).format("MMM YYYY")}
                        </TableCell>
                        <TableCell>
                          {moment(Number(item.end_date)).format("MMM YYYY")}
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            statustype={statusType}
                            label={statusLabel}
                            icon={statusIcon}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography
                        sx={{ color: "#5f6368", fontStyle: "italic", py: 2 }}
                      >
                        No reports found. Request your first report above.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </Box>
      </Fade>
    </PageContainer>
  );
}
